import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import CsvIcon from 'assets/icons/csv.svg';
import { useDropzone } from 'react-dropzone';
import styles from './uploadFile.module.scss';
import { Upload } from 'assets/svgs';
import { Title } from 'components/common/Title';
import { useDispatch, useSelector } from 'react-redux';
import { UPLOAD_FILE, SET_UPLOAD_PROGRESS } from '_redux/types/files.types';
import { filesActions } from '_redux/slices/files.slice';
import { Close } from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const Uploads = ({ onUploadComplete, type = 'collaborations' }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const uploadProgress = useSelector(filesActions.selectors.getUplaodProgress);
  const [category, setCategory] = useState('monthly');
  const dispatch = useDispatch();

  const onDrop = useCallback(
    acceptedFiles => {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      dispatch({ type: SET_UPLOAD_PROGRESS, payload: 0 });
    },
    [dispatch]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.csv, .xlsx',
    maxSize: 50 * 1024 * 1024, // 50MB limit
  });

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);

    try {
      let response;
      switch (type) {
        case 'collaborations':
          response = await fetch(`${API_URL}/api/collaboration/upload`, {
            method: 'POST',
            body: formData,
          });
          break;
        case 'adjustments':
          response = await fetch(`${API_URL}/api/adjustments/upload`, {
            method: 'POST',
            body: formData,
          });
          break;
        case 'advisors':
          response = await fetch(`${API_URL}/api/advisors/upload`, {
            method: 'POST',
            body: formData,
          });
          break;
        case 'introducers':
          response = await fetch(`${API_URL}/api/introducers/upload`, {
            method: 'POST',
            body: formData,
          });
          break;

        default:
          throw new Error('Invalid upload type');
      }

      const data = await response.json();

      if (response.ok) {
        // On successful upload
        setUploading(false);
        onUploadComplete(); // Call the callback function once the upload is complete

        if (data.errorCount == 0) {
          toast.success('Imported Successfully');
        } else {
          toast.error(`${data?.message} with ${data?.errorCount} errors`);
        }
      } else {
        // Handle errors (e.g., invalid rows, file type errors)
        setUploading(false);
        toast.error(`Upload failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setUploading(false);
      console.error('Error uploading file:', error);
      toast.error('Error uploading file');
    }
  };

  const uploadedSize = useMemo(
    () => Math.round((uploadProgress / 100) * (file?.size || 0)),
    [uploadProgress, file]
  );

  const remainingSize = useMemo(
    () => (file ? file.size - uploadedSize : 0),
    [file, uploadedSize]
  );

  return (
    <Box className={styles.uploadContainer}>
      <IconButton className={styles.closeButton} onClick={onUploadComplete}>
        <Close />
      </IconButton>
      <Box sx={{ width: '40%' }}>
        <Title title={'Upload File'} />
      </Box>
      <br />
      <Typography variant="body2" className={styles.subtitle}>
        Upload and attach files to this project.
      </Typography>
      <Box {...getRootProps()} className={styles.fileUploadBox}>
        <input {...getInputProps()} />
        <Upload />
        <Typography variant="body1" color="#000000">
          {isDragActive
            ? 'Drop the files here...'
            : 'Choose a file or drag & drop it here'}
        </Typography>
        <Typography variant="body2" color="#687182">
          Maximum file size 50MB
        </Typography>
      </Box>
      {file && (
        <Box className={styles.fileDetailsBox}>
          <Box className={styles.fileIcon}>
            <img src={CsvIcon} alt="CSV or XLSX Icon" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1" className={styles.fileName}>
              {file.name}
            </Typography>
            <Typography variant="body2" className={styles.fileSize}>
              {`${Math.round(uploadedSize / 1024)} KB uploaded of ${Math.round(file.size / 1024)} KB`}
              • {`${Math.round(remainingSize / 1024)} KB remaining`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              className={styles.progressBar}
            />
          </Box>
          <IconButton
            onClick={() => setFile(null)}
            className={styles.cancelButton}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      )}
      <Button
        variant="contained"
        fullWidth
        startIcon={
          uploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <CloudUploadIcon />
          )
        }
        onClick={handleUpload}
        disabled={!file || uploading}
        className={styles.uploadButton}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </Button>
    </Box>
  );
};

export default Uploads;
